import { useEffect, useRef, useState } from 'react';

export interface SplitDropdownItem {
  id: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  selected?: boolean;
}
export interface SplitDropdownGroup {
  key: string;
  header?: React.ReactNode;
  items: SplitDropdownItem[];
}
export interface SplitDropdownProps {
  ariaLabel: string;
  facePrimary: React.ReactNode;
  faceSecondary?: React.ReactNode;
  faceEmpty?: boolean;
  faceDashed?: boolean | undefined;
  disabled?: boolean;
  groups: SplitDropdownGroup[];
  onFaceClick?: () => void;
  onSelect?: (itemId: string) => void;
  testId?: string;
}

export default function SplitDropdown({
  ariaLabel, facePrimary, faceSecondary, faceEmpty, faceDashed, disabled,
  groups, onFaceClick, onSelect, testId,
}: SplitDropdownProps) {
  const [open, setOpen] = useState(false);
  // Roving-focus index into the flattened option list (nav order == render order).
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

  const flatItems = groups.flatMap(g => g.items);

  // Outside-click closes; Escape closes even if focus escaped the listbox.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // On open, start at the selected option (or first) and move focus onto it.
  useEffect(() => {
    if (!open) return;
    const selIdx = flatItems.findIndex(it => it.selected);
    const start = selIdx >= 0 ? selIdx : 0;
    setActiveIndex(start);
    const raf = requestAnimationFrame(() => optionRefs.current[start]?.focus());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeAndReturnFocus = () => {
    setOpen(false);
    caretRef.current?.focus();
  };

  const focusAt = (i: number) => {
    if (flatItems.length === 0) return;
    const clamped = Math.max(0, Math.min(i, flatItems.length - 1));
    setActiveIndex(clamped);
    optionRefs.current[clamped]?.focus();
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusAt(activeIndex + 1); break;
      case 'ArrowUp': e.preventDefault(); focusAt(activeIndex - 1); break;
      case 'Home': e.preventDefault(); focusAt(0); break;
      case 'End': e.preventDefault(); focusAt(flatItems.length - 1); break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const it = flatItems[activeIndex];
        if (it) { onSelect?.(it.id); closeAndReturnFocus(); }
        break;
      }
      case 'Escape': e.preventDefault(); closeAndReturnFocus(); break;
      default: break;
    }
  };

  let renderIdx = -1; // running index across groups, matching flatItems order

  return (
    // RESPONSIVE SIZING CONTRACT (see `SPLIT_COL_CLASS` in VersionSelector).
    // The control fills its parent column rather than pinning its own width, so
    // the parent decides how wide it gets. The face is the elastic part
    // (`flex-1 min-w-0` — `min-w-0` is what actually lets a flex item shrink
    // below its content and hand the overflow to `truncate`); the caret is
    // `shrink-0` so squeezing never eats the affordance you click.
    <div className="relative flex w-full min-w-0" ref={rootRef} data-testid={testId}>
      <button
        type="button"
        data-testid={testId ? `${testId}-face` : undefined}
        disabled={disabled}
        onClick={() => !disabled && onFaceClick?.()}
        // HEIGHT CONTRACT — this face must be exactly as tall as the Compare
        // `<select>` in the drug header, so the two header rows read as one
        // control strip. Both come to 30px: 4+4 padding + 1+1 border + a 20px
        // line box (Tailwind's `text-sm` sets `line-height:1.25rem`, NOT
        // `leading-normal`'s 1.5). The select cannot reach 30px from padding
        // alone — its internal line box is 19px — so it carries an explicit
        // height; see `DrugDetailPage.tsx`. That is also why primary and
        // secondary share ONE line here rather than stacking: two lines cannot
        // fit 30px without shrinking the type past legibility. Changing `py-1`
        // or the text size breaks the match. Both measured, not derived.
        className={`flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden rounded-l border border-r-0 ${faceDashed ? 'border-dashed ' : ''}border-slate-300 bg-white px-3 py-1 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 ${faceEmpty ? 'text-slate-400 italic' : 'text-slate-700'}`}
      >
        {/* Both spans carry `min-w-0` so either can ellipsize: a flex item
            refuses to shrink below its content without it, which would push the
            secondary out of the box instead of truncating the pair. */}
        <span className="min-w-0 truncate font-semibold">{facePrimary}</span>
        {faceSecondary != null && (
          <span className="min-w-0 truncate text-xs text-slate-500">· {faceSecondary}</span>
        )}
      </button>
      <button
        type="button"
        ref={caretRef}
        aria-label={`${ariaLabel} — open list`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId ? `${testId}-caret` : undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className="flex w-9 shrink-0 items-center justify-center rounded-r border border-slate-300 bg-white text-slate-500 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <svg className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          onKeyDown={onListKeyDown}
          // The menu is pinned to exactly its trigger's width: `min-w-full` is
          // the floor and `max-w-full` the ceiling. BOTH are load-bearing.
          //
          // `max-w-full` is not belt-and-braces. This box is absolutely
          // positioned with `left:0` and `width:auto`, so it is sized
          // shrink-to-fit — `min(max(min-content, available), max-content)` —
          // and min-content can exceed the containing block. Wrapping does NOT
          // save us: `break-words` is `overflow-wrap:break-word`, whose extra
          // break opportunities are excluded from intrinsic min-content sizing
          // by spec (CSS Text 3 §5.5). So one unbroken token — and this app
          // serves plenty, e.g. `sba_split_NDA021436_Orig1s038_...pdf` — blows
          // the menu out to max-content. Measured in headless Chrome under the
          // rightmost of three columns at a 560px viewport: 431px wide, 247px
          // of it off the right edge of the screen, with no way to scroll to
          // it (the box scrolls vertically only). With the cap: 168px, fits,
          // and the token breaks across lines. An earlier revision of this file
          // claimed a cap "can never bind" — that was wrong, and this is the
          // case where it binds.
          className="absolute left-0 top-full z-40 mt-1.5 max-h-80 min-w-full max-w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
        >
          {flatItems.length === 0 && <div className="px-3 py-2 text-xs italic text-slate-400">Nothing to show.</div>}
          {groups.map(g => (
            <div key={g.key}>
              {/* `break-words` on every text node in the menu, not just the
                  option primary: the box is now width-capped, so anything that
                  refuses to wrap gets an inner horizontal scrollbar instead. */}
              {g.header != null && <div className="sticky top-0 break-words bg-white px-2.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">{g.header}</div>}
              {g.items.map(it => {
                renderIdx += 1;
                const myIdx = renderIdx;
                return (
                  <div
                    key={it.id}
                    role="option"
                    aria-selected={!!it.selected}
                    tabIndex={myIdx === activeIndex ? 0 : -1}
                    ref={(el) => { optionRefs.current[myIdx] = el; }}
                    onClick={() => { onSelect?.(it.id); closeAndReturnFocus(); }}
                    className={`flex cursor-pointer flex-col gap-0.5 rounded-lg px-2.5 py-2 text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${it.selected ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-700'}`}
                  >
                    {/* Wraps rather than `whitespace-nowrap`: with the menu now
                        width-capped, a nowrap name would spill sideways out of
                        a box that only scrolls vertically. */}
                    <span className="break-words">{it.primary}</span>
                    {it.secondary != null && <span className="break-words text-[11px] font-normal text-slate-400">{it.secondary}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
